<template>
  <base-modal ref="modal" @hiding="reset()">
    <base-card shadow>
      <h3 class="tw-text-xl tw-font-thin">Edit Plan</h3>
      <base-form ref="form" v-model="isFormValid" class="tw-mt-5">
        <base-text-field label="Custom plan name" placeholder="Enter custom plan name" required v-model="newPlanName" :readonly="isLoading" />
        <base-date-picker
          label="Change Cycle Date"
          placeholder="Select new billing cycle date"
          v-model="newCycleEndDate"
          required
          :disabledDates="disabledDates"
          :typeable="true"
          :inline="false"
        />
        <div class="tw-mt-3" v-if="plan.planType === 'subscription'">
          <div class="tw-flex">
            <base-toggle label="Auto Renew Plan" v-model="newIsAutoRenew" />
            <span class="tw-text-gray-500 tw-text-base tw-font-sm tw-ml-2">Auto-renew?</span>
          </div>
          <div class="tw-mt-6" v-if="!newIsAutoRenew">
            <base-date-picker
              label="Plan End Date (The date the plan gets cancelled)"
              v-model="newCancelAfterDate"
              :disabledDates="disabledTermEndDates"
              :typeable="true"
              :inline="false"
            />
          </div>
        </div>
        <div v-else>
          <base-date-picker label="End Date (The date the plan gets cancelled)" v-model="newCancelAfterDate" :disabledDates="disabledEndDates" :typeable="true" :inline="false" />
        </div>
      </base-form>
      <div class="sm:tw-flex sm:tw-flex-row-reverse tw-mt-4">
        <base-button @click="updatePlan" :loading="isLoading" class="tw-w-full sm:tw-w-auto sm:tw-ml-3" :disabled="!isFormValid">Save</base-button>
        <base-button @click="hide" type="flat" class="tw-w-full sm:tw-w-auto tw-mt-2 sm:tw-mt-0">
          Cancel
        </base-button>
      </div>
      <inline-error id="edit-business-member-plan-modal-error" :title="errorTitle" :message="errorMessage" :error="error" />
    </base-card>
  </base-modal>
</template>
<script>
import moment from "moment";
import InlineError from "@/components/InlineError";

export default {
  name: "BusinessMemberPlanEdit",
  components: { InlineError },
  props: {
    businessId: String,
    userId: String,
    plan: Object,
  },
  data() {
    return {
      isLoading: false,
      isFormValid: true,
      errorTitle: "",
      errorMessage: "",
      error: null,

      newCycleEndDate: null,
      newPlanName: null,
      newPaymentCycleEndDate: null,
      newCancelAfterDate: null,
      newIsAutoRenew: null,
      planCancelDateValidation: false,
    };
  },
  computed: {
    disabledDates() {
      var min = new Date();
      var max = new Date();
      min.setDate(min.getDate() - 1);

      if (this.plan.cycleInterval === "year") max.setFullYear(max.getFullYear() + this.plan.cycleFrequency);
      else if (this.plan.cycleInterval === "month") max.setMonth(max.getMonth() + this.plan.cycleFrequency);
      else if (this.plan.cycleInterval === "week") max.setDate(max.getDate() + this.plan.cycleFrequency * 7);
      else if (this.plan.cycleInterval === "day") max.setDate(max.getDate() + this.plan.cycleFrequency);

      return { to: min, from: max };
    },
    disabledEndDates() {
      var min = new Date();
      min.setDate(min.getDate() - 1);
      return { to: min };
    },
    disabledTermEndDates() {
      let min = new Date();

      if (moment(this.plan.termEndDate) > moment(new Date())) min = new Date(this.plan.termEndDate);

      return { to: min };
    },
    hasChanges: {
      get() {
        if (
          this.newCycleEndDate != this.plan.currentCycleEndDate ||
          this.newPlanName != this.plan.planName ||
          this.newPaymentCycleEndDate != this.plan.paymentCycleEndDate ||
          this.newCancelAfterDate != this.plan.cancelAfterDate ||
          this.newIsAutoRenew != this.plan.isAutoRenew
        )
          return true;
        else return false;
      },
    },
  },
  watch: {
    newIsAutoRenew: {
      handler: function(newFlag) {
        if (newFlag === true) {
          this.newCancelAfterDate = null;
          this.newPaymentCycleEndDate = null;
          this.planCancelDateValidation = false;
        }
      },
    },
  },
  methods: {
    show() {
      if (this.plan.currentPeriodEnd)
        this.newCycleEndDate = moment
          .utc(this.plan.currentPeriodEnd)
          .local()
          .format("MMM DD, YYYY h:mm a");

      this.newPlanName = this.plan.planName;

      if (this.plan.paymentCycleEndDate)
        this.newPaymentCycleEndDate = moment
          .utc(this.plan.paymentCycleEndDate)
          .local()
          .format("MMM DD, YYYY h:mm a");

      if (this.plan.cancelAfterDate)
        this.newCancelAfterDate = moment
          .utc(this.plan.cancelAfterDate)
          .local()
          .format("MMM DD, YYYY h:mm a");
      this.newIsAutoRenew = this.plan.isAutoRenew;

      this.$refs.modal.show();
    },
    hide() {
      this.$refs.modal.hide();
    },
    reset() {
      this.$refs.form.reset();
    },
    updatePlan() {
      if (!this.$refs.form.validate()) return;

      if (this.newPaymentCycleEndDate < new Date(1990, 1, 1)) this.newPaymentCycleEndDate = null;

      if (this.hasChanges) {
        this.isLoading = true;

        this.$axios
          .put(`/businesses/${this.businessId}/members/${this.userId}/memberplans/${this.plan.userBusinessPlanId}`, {
            anchorDate: this.newCycleEndDate,
            planName: this.newPlanName,
            isAutoRenew: this.newIsAutoRenew,
            cancelAfterDate: this.newCancelAfterDate,
            paymentCycleEndDate: this.newPaymentCycleEndDate,
          })
          .then(() => {
            this.EventBus.$emit("member_update");
            this.$emit("refresh");
            this.isLoading = false;
            this.hide();
            this.EventBus.$emit("GLOBAL_NOTIFY", { title: "Member Plan Updated", message: "Plan was updated successfully" });
          })
          .catch((err) => {
            this.isLoading = false;
            this.errorTitle = `Error editing the plan: ${this.plan.planName}`;
            this.errorMessage = "Sorry, we had trouble editing this plan. The technicians have been notified. Please try again.";
            this.error = err;
          });
      } else {
        this.hide();
      }
    },
  },
};
</script>
